'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@7f-ui/components/button/button';
import { Price } from '@7f-ui/components/price';
import { Skeleton } from '@7f-ui/components/skeleton/skeleton';
import { useMediaState } from '@7f-ui/hooks/use-media-state';
import type { Cart } from '@feat/feat-cart/cart';
import { getTouristTaxType } from '@feat/feat-cart/pricing';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { lazy, Suspense } from 'react';
import { CartCTABoxContent } from './cart-cta-box-content';
import { CartTotalSummary } from './cart-total-summary';
import { CTASecurity } from './cta-security';
const CartCTABoxMobileLazy = lazy(() => import('./cart-cta-box-mobile').then(mod => ({
  default: mod.CartCTABoxMobile
})));
const TOURIST_TAX_TYPE_LABELS: Record<string, string> = {
  CityTax: 'Übernachtungssteuer',
  SpaTax: 'Kurtaxe'
};
type CartCTABoxProps = {
  cart: Cart;
  checkoutHref: string;
};
export function CartCTABox(t0) {
  const $ = _c(4);
  const {
    cart,
    checkoutHref
  } = t0;
  const isMobile = useMediaState("mobile");
  let t1;
  if ($[0] !== cart || $[1] !== checkoutHref || $[2] !== isMobile) {
    t1 = isMobile ? <Suspense fallback={<CartCTABoxMobileLoading cart={cart} />}><CartCTABoxMobileLazy cart={cart} checkoutHref={checkoutHref} /></Suspense> : <CartCTABoxDesktop cart={cart} checkoutHref={checkoutHref} />;
    $[0] = cart;
    $[1] = checkoutHref;
    $[2] = isMobile;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function CartCTABoxDesktop(t0) {
  const $ = _c(11);
  const {
    cart,
    checkoutHref
  } = t0;
  let t1;
  if ($[0] !== cart) {
    t1 = <CartTotalSummary cart={cart} />;
    $[0] = cart;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== cart || $[3] !== checkoutHref) {
    t2 = <CartCTABoxContent.SubmitButton cart={cart} checkoutHref={checkoutHref} />;
    $[2] = cart;
    $[3] = checkoutHref;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <CartCTABoxContent>{t1}{t2}</CartCTABoxContent>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CTASecurity />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3) {
    t5 = <div className="col-span-4 col-start-9"><div className="sticky-top-offset-6 sticky"><div className="flex flex-col gap-4 rounded-2xl border border-primary/15 p-6 shadow lg:p-8">{t3}{t4}</div></div></div>;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function CartCTABoxMobileLoading(t0) {
  const $ = _c(20);
  const {
    cart
  } = t0;
  let t1;
  if ($[0] !== cart) {
    t1 = getTouristTaxType(cart);
    $[0] = cart;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const touristTaxType = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <LoadingNotice />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton className="h-7 w-36" />;
    t4 = <Skeleton className="h-5 w-full" />;
    t5 = <Skeleton className="h-5 w-10/12" />;
    t6 = <Skeleton className="h-5 w-11/12" />;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="col-span-12 mt-6"><div className="rounded-2xl border border-primary/15 p-6 shadow-sm">{t2}<div className="space-y-3">{t3}{t4}{t5}{t6}<div className="pt-4"><Skeleton className="h-11 w-full rounded-full" /></div></div></div></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="font-heading4">Gesamtpreis</span>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== cart.currency || $[10] !== cart.total) {
    t9 = <div className="flex items-baseline justify-between gap-4">{t8}<Price amount={cart.total} currency={cart.currency} className="font-heading4" /></div>;
    $[9] = cart.currency;
    $[10] = cart.total;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== touristTaxType) {
    t10 = <div className="font-base-caption">{touristTaxType ? <span>{`inkl. MwSt. zzgl. ${TOURIST_TAX_TYPE_LABELS[touristTaxType] ?? touristTaxType}`}</span> : <span className="text-success">Alle Steuern und Gebühren inklusive</span>}</div>;
    $[12] = touristTaxType;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Button full={true} disabled={true} data-testid={shopTestId("CTABox.checkout-cta")}>Weiter</Button>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t11) {
    t13 = <>{t7}<div className="fixed inset-x-0 bottom-0 z-30 shadow-sticky-footer sm:hidden"><div className="flex flex-col gap-3 bg-white px-4 xs:px-6 py-4 text-primary">{t11}{t12}</div></div></>;
    $[18] = t11;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  return t13;
}
function LoadingNotice() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-4 flex items-center gap-3 text-primary/80"><span className="size-2 animate-pulse rounded-full bg-primary/35" /><span className="font-body-small font-semibold">Laden</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}